import { Injectable, inject } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import {
  Action,
  NgxsAfterBootstrap,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { catchError, finalize, Observable, of, tap } from 'rxjs';
import {
  InitializeVehicleClassesState,
  VehicleEmissionClassesInitialize,
} from './vehicle-classes.actions';
import { VehicleEmissionClassesStateModel } from '../types';
import { VehiclesGatewayService } from '@wilson/vehicles/core';
import { VehicleEmissionClasses } from '@wilson/interfaces';

const STATE_KEY = 'vehicleEmissionClasses';

const defaults: VehicleEmissionClassesStateModel = {
  isLoading: false,
  selectableVehicleEmissionClasses: [],
};

@State<VehicleEmissionClassesStateModel>({
  name: STATE_KEY,
  defaults,
})
@Injectable()
export class VehicleClassesState implements NgxsAfterBootstrap {
  private readonly vehiclesGatewayService = inject(VehiclesGatewayService);
  private readonly storage = inject(Storage);

  @Selector()
  static selectableVehicleEmissionClasses(
    state: VehicleEmissionClassesStateModel,
  ): VehicleEmissionClasses[] {
    return state.selectableVehicleEmissionClasses;
  }

  @Selector()
  static isLoading(state: VehicleEmissionClassesStateModel): boolean {
    return state.isLoading;
  }

  async ngxsAfterBootstrap(
    ctx: StateContext<VehicleEmissionClassesStateModel>,
  ): Promise<void> {
    const stored: VehicleEmissionClasses[] | null =
      await this.storage.get(STATE_KEY);
    if (stored?.length) {
      ctx.patchState({ selectableVehicleEmissionClasses: stored });
    }
  }

  @Action(InitializeVehicleClassesState)
  initialize(
    ctx: StateContext<VehicleEmissionClassesStateModel>,
  ): Observable<VehicleEmissionClasses[]> {
    ctx.patchState({ isLoading: true });
    return this.vehiclesGatewayService.getVehicleClasses().pipe(
      tap((data: VehicleEmissionClasses[]) => {
        ctx.patchState({ selectableVehicleEmissionClasses: data });
        this.storage.set(STATE_KEY, data);
      }),
      catchError(() => of([])),
      finalize(() => {
        ctx.patchState({ isLoading: false });
      }),
    );
  }

  /**
   * @deprecated Use `InitializeVehicleClassesState` instead.
   * Kept for backwards compatibility with portal consumers.
   */
  @Action(VehicleEmissionClassesInitialize)
  initializeLegacy(
    ctx: StateContext<VehicleEmissionClassesStateModel>,
  ): Observable<VehicleEmissionClasses[]> {
    return this.initialize(ctx);
  }
}
